export type DeviceToolErrorCode =
  | "WEBUSB_UNSUPPORTED"
  | "INSECURE_CONTEXT"
  | "DEVICE_NOT_SELECTED"
  | "DEVICE_BUSY"
  | "INTERFACE_NOT_FOUND"
  | "CLAIM_INTERFACE_FAILED"
  | "USB_ACCESS_DENIED"
  | "DEVICE_DISCONNECTED"
  | "TRANSFER_TIMEOUT"
  | "TRANSFER_STALL"
  | "ADB_AUTH_REQUIRED"
  | "ADB_COMMAND_FAILED"
  | "FASTBOOT_COMMAND_FAILED"
  | "INVALID_IMAGE"
  | "UNSUPPORTED_IMAGE"
  | "AMLOGIC_HANDSHAKE_FAILED"
  | "OPERATION_CANCELLED";

const USER_MESSAGES: Record<DeviceToolErrorCode, string> = {
  WEBUSB_UNSUPPORTED: "Trình duyệt này không hỗ trợ WebUSB. Hãy dùng Chrome hoặc Edge desktop.",
  INSECURE_CONTEXT: "WebUSB chỉ hoạt động trên HTTPS hoặc localhost.",
  DEVICE_NOT_SELECTED: "Chưa chọn thiết bị USB.",
  DEVICE_BUSY: "Thiết bị hoặc interface USB đang được ứng dụng khác sử dụng.",
  INTERFACE_NOT_FOUND: "Không tìm thấy interface USB phù hợp với giao thức này.",
  CLAIM_INTERFACE_FAILED: "Không thể claim interface USB. Kiểm tra driver và ứng dụng đang chiếm thiết bị.",
  USB_ACCESS_DENIED: "Windows đang từ chối quyền mở USB cho trình duyệt.",
  DEVICE_DISCONNECTED: "Thiết bị đã ngắt kết nối trong khi thao tác đang chạy.",
  TRANSFER_TIMEOUT: "Thiết bị không phản hồi trong thời gian cho phép.",
  TRANSFER_STALL: "USB transfer bị stall. Có thể thử kết nối lại thiết bị.",
  ADB_AUTH_REQUIRED: "Thiết bị yêu cầu xác thực RSA. Hãy mở khóa thiết bị và chấp nhận hộp thoại USB debugging.",
  ADB_COMMAND_FAILED: "Lệnh ADB thất bại. Xem log để biết phản hồi từ thiết bị.",
  FASTBOOT_COMMAND_FAILED: "Lệnh Fastboot bị thiết bị từ chối. Không tự động bỏ qua AVB, secure boot hoặc anti-rollback.",
  INVALID_IMAGE: "Image không hợp lệ hoặc bị cắt ngắn.",
  UNSUPPORTED_IMAGE: "Định dạng image này chưa được hỗ trợ an toàn.",
  AMLOGIC_HANDSHAKE_FAILED: "Handshake Amlogic không hợp lệ. Flash đã bị dừng an toàn.",
  OPERATION_CANCELLED: "Thao tác đã được hủy.",
};

export class DeviceToolError extends Error {
  readonly code: DeviceToolErrorCode;
  readonly suggestion?: string;
  readonly cause?: unknown;

  constructor(code: DeviceToolErrorCode, detail?: string, suggestion?: string, cause?: unknown) {
    super(detail ? `${USER_MESSAGES[code]} ${detail}` : USER_MESSAGES[code]);
    this.name = "DeviceToolError";
    this.code = code;
    this.suggestion = suggestion;
    this.cause = cause;
  }
}

export function toDeviceToolError(error: unknown, fallback: DeviceToolErrorCode): DeviceToolError {
  if (error instanceof DeviceToolError) return error;
  if (error instanceof DOMException && error.name === "NotFoundError") {
    return new DeviceToolError("DEVICE_NOT_SELECTED", undefined, "Chọn lại thiết bị từ nút Kết nối.", error);
  }
  return new DeviceToolError(fallback, error instanceof Error ? error.message : String(error), undefined, error);
}

/** The browser throws this when the user closes the WebUSB picker without choosing a device. */
export function isUsbPickerCancelled(error: unknown): boolean {
  if (error instanceof DeviceToolError) return error.code === "DEVICE_NOT_SELECTED";
  if (!error || typeof error !== "object") return false;
  const candidate = error as { name?: unknown; message?: unknown };
  return candidate.name === "NotFoundError"
    || (typeof candidate.message === "string" && /\bNo device selected\b/iu.test(candidate.message));
}

export function userMessage(error: unknown): string {
  return error instanceof DeviceToolError ? error.message : error instanceof Error ? error.message : String(error);
}
